import { useCallback, useRef } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { ActivityItem } from 'app/data/types/activity';
import { useApi } from 'app/shared/contexts/api';

import {
  CurrentUserActivityResponse,
  fetchActivityPage,
  resolveSearchUrl,
  Segment,
} from './use-activity-feed-query.hook';
import { ACTIVITY_FEED_QUERY_KEY } from './use-refetch-activity-feed.hook';

/**
 * How long a sync may run before the queue stops waiting on it.
 *
 * Generous on purpose: this is not a request deadline, it is a wedge-breaker. Every read it covers is
 * one the feed makes normally, so anything still outstanding at this point is stalled rather than slow.
 */
export const SYNC_TIMEOUT_MS = 20_000;

/** `[key, segment, type, searchUrl, locale]` — the feed queries; the badge-total key is shorter. */
type FeedQueryKey = [string, Segment, string, string, string];

/**
 * Apply a stream update to the feed without the churn — or the spinner — of an invalidation.
 *
 * `invalidateQueries` is the wrong tool for a push. The feed is cursor-paginated, and a refetch re-runs
 * the whole page CHAIN from the first page: page two is fetched with a cursor derived from the
 * REFETCHED page one. A frame arrives precisely because something at the head changed, so every
 * downstream window slides, and `flattenPages` dedupes the overlap into a list of a different length
 * than the one on screen — rows appearing and vanishing that nothing actually happened to. It also
 * flips `isFetching`, which gates the load-more sentinel and re-fires page impressions.
 *
 * So each loaded page is re-read at the cursor it was ALREADY fetched with, and the results replace
 * the held pages as they are. `pageParams` is untouched, because nothing was re-derived.
 *
 * That anchoring is the whole trick: a cursor is a position in sort space rather than a row, so it
 * stays meaningful even once the row that produced it has moved on. Each window is therefore re-read
 * where it was, and what comes back is the server's current view of the same span — new activity
 * inside it appears, changed rows update, anything that left is gone, and nothing moves that did not
 * genuinely change. Writing through `setQueryData` keeps all of it out of the query's status, so
 * nothing anywhere renders a spinner.
 *
 * The Upcoming badge is deliberately left alone: it is re-read on the next natural refresh, and it is
 * a count beside the tabs rather than part of the list.
 *
 * Mutations keep using {@link useRefetchActivityFeed}: there the user just acted, so a full settle is
 * expected feedback rather than something moving on its own.
 */
export const useSyncActivityFeed = (): (() => void) => {
  const queryClient = useQueryClient();
  const { apiUrl, getAuthHeaders } = useApi();
  const { locale } = useIntl();
  const searchUrl = apiUrl ? resolveSearchUrl(apiUrl) : '';

  /*
   * Syncs run ONE at a time, with at most one follow-up queued behind the current run.
   *
   * Overlapping them loses data to the `dataUpdatedAt` guard, and loses the NEWER half. Two syncs that
   * start close together both capture the same `updatedAt`; the first to finish writes and moves it,
   * so the second — which started later and therefore read fresher rows — sees the guard trip and
   * discards itself. The feed would sit on the older of two results it had already paid for.
   *
   * Serializing removes the overlap entirely, and the queued run re-reads AFTER the write, so it
   * captures the moved `updatedAt` and its own write lands. Any number of requests arriving mid-run
   * coalesce into that single follow-up — a burst does not become a queue of fetches.
   */
  const inFlight = useRef(false);
  const queued = useRef(false);

  return useCallback(() => {
    const sync = async (): Promise<void> => {
      if (!searchUrl) {
        return;
      }
      const headers = await getAuthHeaders();

      /*
       * Only what someone is looking at — an unmounted variant refetches on its next mount anyway.
       *
       * Observer COUNT, deliberately, not `findAll({ type: 'active' })`. "Active" in v5 means a query
       * has an observer whose `enabled` is not false, which is a question about fetching. This one is
       * about display: a mounted query with `enabled: false` still renders the pages it holds, so its
       * rows still need patching or they sit there stale.
       */
      const feeds = queryClient
        .getQueryCache()
        .findAll({ queryKey: [ACTIVITY_FEED_QUERY_KEY] })
        .filter((query) => query.getObserversCount() > 0);

      const loaded = feeds
        // No `pages` = the badge-total query, which is a plain number and not ours to patch.
        // `updatedAt` is taken NOW, before the awaits, so a write that lands during them is detectable.
        .map((query) => ({
          query,
          cached: query.state.data as InfiniteData<CurrentUserActivityResponse> | undefined,
          updatedAt: query.state.dataUpdatedAt,
        }))
        .filter((entry): entry is {
          query: typeof entry.query;
          cached: InfiniteData<CurrentUserActivityResponse>;
          updatedAt: number;
        } => !!entry.cached?.pages?.length);

      const reread = await Promise.all(
        loaded.map(({ query, cached }) => {
          const [, segment, type, , keyLocale] = query.queryKey as FeedQueryKey;
          return Promise.all(
            cached.pages.map((_, index) => fetchActivityPage({
              headers,
              searchUrl,
              segment,
              type: type === 'all' ? undefined : (type as ActivityItem['type']),
              locale: keyLocale ?? locale,
              cursor: cached.pageParams[index] as string | undefined,
            })),
          );
        }),
      );

      loaded.forEach(({ query, updatedAt }, index) => {
        /*
         * `cached` is a snapshot from BEFORE the re-reads, and those take real network time. A
         * `fetchNextPage` from the scroll sentinel, a mutation's refetch, or another sync can all land
         * in that window. Anything that wrote during it is newer than what we are holding, so it wins
         * outright and this result is dropped rather than reverting it.
         */
        if (query.state.dataUpdatedAt !== updatedAt) {
          return;
        }

        // Updater form, so the write applies to what is in the cache at write time rather than to the
        // snapshot — the guard above and this are two halves of the same point.
        queryClient.setQueryData(query.queryKey, (current) => {
          const live = current as InfiniteData<CurrentUserActivityResponse> | undefined;
          if (!live?.pages?.length) return current;

          /*
           * The re-read pages replace the held ones outright, and `pageParams` is untouched because
           * each page was re-read at the very cursor it was fetched with.
           *
           * That anchoring is the whole trick. `invalidateQueries` re-runs the page CHAIN — page two
           * fetched with a cursor derived from the refetched page one — so when the head has changed,
           * which is exactly when a frame arrives, every downstream window slides and `flattenPages`
           * dedupes the overlap into a list of a different length. Re-reading at the ORIGINAL cursors
           * holds each window where it was: a cursor is a position in sort space, not a row, so it
           * stays meaningful even when the row that produced it has moved on.
           *
           * So this is the server's current view of the same windows. New activity inside them
           * appears, changed rows update, and anything that left is gone — without the churn.
           */
          /*
           * Indexed against what is LIVE, not a blind replace. The re-read covers the pages held when
           * it started; if `fetchNextPage` appended one since, that page has no counterpart here and
           * must be kept rather than truncated away. The `dataUpdatedAt` guard above catches most of
           * those, but not two writes landing in the same millisecond — and losing a page the user
           * scrolled to is the one outcome worth being belt-and-braces about.
           */
          return { ...live, pages: live.pages.map((page, pageIndex) => reread[index][pageIndex] ?? page) };
        });
      });
    };

    const run = (): void => {
      inFlight.current = true;
      let released = false;

      const release = (): void => {
        if (released) return;
        released = true;
        inFlight.current = false;
        if (queued.current) {
          queued.current = false;
          run();
        }
      };

      /*
       * The in-flight flag is released on a WATCHDOG as well as on settlement.
       *
       * `apiRequest` carries no timeout, so a single read that never settles — a socket a sleeping
       * laptop left open, a connection a network change stranded — leaves this promise pending
       * forever. Without the watchdog `inFlight` would stay true for the life of the hook and every
       * frame after it would be dropped in silence: the feed stops updating with a live stream, no
       * error, and nothing to suggest why. Serializing bought correctness against overlap and paid
       * for it with that failure mode; this is the other half.
       *
       * A late-arriving run can still write, and the `dataUpdatedAt` guard keeps it from clobbering
       * anything newer.
       */
      const watchdog = setTimeout(() => {
        release();
      }, SYNC_TIMEOUT_MS);

      // A failed sync leaves the list exactly as it was, which is the right outcome for a background
      // update the user never asked for.
      sync()
        .catch(() => undefined)
        .finally(() => {
          clearTimeout(watchdog);
          release();
        });
    };

    if (inFlight.current) {
      queued.current = true;
      return;
    }

    run();
  }, [queryClient, getAuthHeaders, searchUrl, locale]);
};
